import { useEffect } from 'react';
import { RouterProvider, type RouterProviderProps } from 'react-router';
import { api } from '~/api.ts';
import { AuthContext, useAuth } from '~/auth';
import Loading from '~/components/ui/Loading';
import { ROUTER, ROUTER_TERMS_NOT_ACCEPTED, ROUTES } from '~/routes.tsx';
import '~/styles/styles.scss';
import { isProduction } from '~/utils/env.ts';
import storage from '~/utils/storage.ts';

export default function App({
    createRouter,
}: {
    createRouter?: (routes: typeof ROUTES) => RouterProviderProps['router'];
}) {
    const auth = useAuth();

    useEffect(() => {
        if (!createRouter && !auth.loading && !auth.authenticated) {
            setupTurnstile();
        }
    }, [createRouter, auth.loading, auth.authenticated]);

    if (auth.loading && !window.location.pathname.startsWith('/apply')) return <Loading />;

    let router;
    if (createRouter) {
        router = createRouter(ROUTES);
    } else if (auth.authenticated && !auth.user.terms_accepted) {
        router = ROUTER_TERMS_NOT_ACCEPTED;
    } else {
        router = ROUTER;
    }

    return (
        <AuthContext.Provider value={auth}>
            <RouterProvider router={router} />
        </AuthContext.Provider>
    );
}

async function turnstileValidate(token: string) {
    return api.auth<boolean>('/turnstile', { method: 'POST', body: { cf_turnstile_token: token } });
}

async function diagnosticsErrorPage() {
    return api<boolean>('/diagnostics/error-page', { method: 'POST' });
}

function setupTurnstile() {
    const widgetSelector = '#turnstile-widget';
    if (
        !Object.hasOwn(window, 'turnstile') ||
        document.location.pathname === '/error' ||
        !document.querySelector(widgetSelector)
    ) {
        return;
    }

    // https://developers.cloudflare.com/turnstile/troubleshooting/testing/#test-sitekeys
    const sitekey = isProduction() ? '0x4AAAAAACB1hJ7P_n8pPBhC' : '1x00000000000000000000BB';
    window.turnstile.render(widgetSelector, {
        sitekey,
        callback: function (token: string) {
            void turnstileValidate(token);
        },
        'error-callback': () => {
            storage.setItem('return_to', window.location.pathname);
            try {
                void diagnosticsErrorPage();
            } catch (err) {
                console.warn('Failed to report error page visit', err);
            }
            document.location.href = '/error';
        },
    });
    return null;
}
